<script setup>
// RM-427: the application exactly as an applicant would see it, from settings
// that may not be saved yet (POST /application/admin/preview writes nothing).
// Every screen is shown in order at phone width; answers can be typed so
// follow-up questions appear as they would for an applicant.
import { reactive, ref, watch } from 'vue'
import axios from '@axios'
import ApplicationStepFields from '@core/components/application/ApplicationStepFields.vue'
import { STEP_TITLES } from '@/utils/applicationForm'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  // { path, pathQuestions?, groupId?, groupOverrides? }
  request: { type: Object, default: null },
  title: { type: String, default: 'Preview' },
})

const emit = defineEmits(['update:modelValue'])

const form = ref(null)
const loading = ref(false)
const error = ref('')
const answers = reactive({})
const smsOptIn = ref(false)

const load = async () => {
  if (!props.request)
    return
  loading.value = true
  error.value = ''
  form.value = null
  Object.keys(answers).forEach(k => delete answers[k])
  try {
    const { data } = await axios.post('/application/admin/preview', props.request)
    form.value = data
  } catch (e) {
    error.value = e?.response?.data?.error || 'Could not build the preview'
  } finally {
    loading.value = false
  }
}

watch(() => props.modelValue, open => {
  if (open)
    load()
})
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="460"
    scrollable
    @update:model-value="v => emit('update:modelValue', v)"
  >
    <VCard>
      <VCardTitle class="d-flex align-center pt-4">
        <span class="text-h6">{{ title }}</span>
        <VSpacer />
        <VBtn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="emit('update:modelValue', false)"
        />
      </VCardTitle>
      <VCardSubtitle class="preview-subtitle">
        As an applicant sees it. Nothing here is saved.
      </VCardSubtitle>
      <VCardText class="preview-body">
        <VProgressLinear
          v-if="loading"
          indeterminate
          color="primary"
        />
        <VAlert
          v-else-if="error"
          type="error"
          variant="tonal"
        >
          {{ error }}
        </VAlert>
        <template v-else-if="form">
          <section
            v-for="step in form.steps"
            :key="step"
            class="preview-step"
          >
            <h6 class="preview-step__title">
              {{ STEP_TITLES[step] }}
            </h6>
            <ApplicationStepFields
              :form="form"
              :step="step"
              :answers="answers"
              :sms-opt-in="smsOptIn"
              @answer="(k, v) => { answers[k] = v }"
              @update:sms-opt-in="v => smsOptIn = v"
            />
          </section>
          <p
            v-if="!form.smsOptIn || !form.smsOptIn.enabled"
            class="preview-note"
          >
            The RM via text choice is switched off, so applicants don't see it.
          </p>
        </template>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.preview-subtitle {
  white-space: normal;
}

.preview-body {
  max-height: 75vh;
}

.preview-step + .preview-step {
  margin-top: 24px;
  padding-top: 16px;
  border-top: thin solid var(--ds-border-light);
}

.preview-step__title {
  margin-bottom: 16px;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--ds-text-strong);
}

.preview-note {
  margin-top: 16px;
  font-size: 14px;
  color: var(--ds-text-muted);
}
</style>
